import { useState } from "react";
import { Exercise, LmsApiService } from "../backend";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { CheckCircle2, XCircle, HelpCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/features/auth/context/AuthContext";
import { useToast } from "@/hooks/use-toast";

interface ExerciseBlockProps {
  exercise: Exercise;
  courseId: number;
  onComplete?: (isCorrect: boolean) => void;
}

const lmsService = new LmsApiService();

export default function ExerciseBlock({
  exercise,
  courseId,
  onComplete
}: ExerciseBlockProps) {
  const { profile } = useAuth();
  const { toast } = useToast();

  const [selectedOption, setSelectedOption] = useState<string>("");
  const [textAnswer, setTextAnswer] = useState<string>("");
  const [submitted, setSubmitted] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!profile?.id) return;

    const answer =
      exercise.type === "multiple_choice" ? Number(selectedOption) : textAnswer;

    if (!answer) {
      toast({
        title: "Please provide an answer",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const submission = await lmsService.submitExercise(
        profile.id,
        courseId,
        exercise.id,
        answer
      );
      setSubmitted(true);
      setIsCorrect(submission.is_correct);
      if (onComplete) onComplete(submission.is_correct);
    } catch (error) {
      console.error("Submission failed", error);
      toast({
        title: "Error submitting answer",
        description: "Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    setSubmitted(false);
    setIsCorrect(false);
    setSelectedOption("");
    setTextAnswer("");
  };

  return (
    <Card className="my-8 border-l-4 border-l-[#471999]">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <HelpCircle className="w-5 h-5 text-[#471999]" />
          Exercise
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="font-medium text-gray-800">{exercise.prompt}</p>

        {!submitted ? (
          <div className="mt-4">
            {exercise.type === "multiple_choice" && (
              <RadioGroup
                value={selectedOption}
                onValueChange={setSelectedOption}
              >
                {exercise.data.options.map((option: any) => (
                  <div
                    key={option.id}
                    className="flex items-center space-x-2 p-2 rounded hover:bg-gray-50"
                  >
                    <RadioGroupItem
                      value={String(option.id)}
                      id={`opt-${option.id}`}
                    />
                    <Label
                      htmlFor={`opt-${option.id}`}
                      className="flex-1 cursor-pointer"
                    >
                      {option.text}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            )}

            {exercise.type === "open_text" && (
              <Input
                placeholder="Type your answer here..."
                value={textAnswer}
                onChange={(e) => setTextAnswer(e.target.value)}
              />
            )}
          </div>
        ) : (
          <div
            className={cn(
              "p-4 rounded-md flex items-center gap-3",
              isCorrect
                ? "bg-green-50 text-green-800"
                : "bg-red-50 text-red-800"
            )}
          >
            {isCorrect ? (
              <CheckCircle2 className="w-6 h-6" />
            ) : (
              <XCircle className="w-6 h-6" />
            )}
            <div>
              <p className="font-bold">
                {isCorrect ? "Correct!" : "Incorrect"}
              </p>
              <p className="text-sm">
                {isCorrect
                  ? "Great job! You can proceed."
                  : "That wasn't quite right. Try again?"}
              </p>
            </div>
          </div>
        )}
      </CardContent>
      <CardFooter>
        {!submitted ? (
          <Button
            onClick={handleSubmit}
            disabled={loading}
            className="bg-[#471999] hover:bg-[#38147a]"
          >
            {loading ? "Checking..." : "Submit Answer"}
          </Button>
        ) : (
          !isCorrect && (
            <Button variant="outline" onClick={handleRetry}>
              Try Again
            </Button>
          )
        )}
      </CardFooter>
    </Card>
  );
}
